import { useRouter } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Card, CardDescription, CardHeader, CardTitle } from "design-system/molecules/Card"
import { ArchiveRestore, Pause } from "lucide-react"
import { useGetBackofficeScheduledReportQuery } from "modules/backoffice/hooks/scheduledReports/useGetBackofficeScheduledReportQuery"
import { useUpsertBackofficeScheduledReportsMutation } from "modules/backoffice/hooks/scheduledReports/useUpsertBackofficeScheduledReportsMutation"
import { HeaderButtons } from "modules/shared/components/Breadcrumbs/HeaderButtons"
import { Loading } from "modules/shared/components/Loading"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeScheduledReportsSearchSchema } from "routes/_base/_auth/backoffice/scheduledReports/list/route"
import { UpsertBackofficeScheduledReportSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { BackofficeScheduledReportForm } from "./BackofficeScheduledReportForm"

type Props = {
  scheduledReportId: string
}
export const BackofficeScheduledReportEditor = ({ scheduledReportId }: Props) => {
  const { t } = useTranslation()
  const { navigate } = useRouter()
  const [error, setError] = useState<string | null>(null)
  const { data: scheduledReport, isPending } = useGetBackofficeScheduledReportQuery({
    id: scheduledReportId,
  })
  const { mutateAsync, isPending: mutateIsPending } = useUpsertBackofficeScheduledReportsMutation({
    id: scheduledReportId,
  })

  const onSubmit = async (data: z.input<typeof UpsertBackofficeScheduledReportSchema>) => {
    setError(null)
    const response = await mutateAsync(data)

    if ("error" in response.data) {
      setError(response.data.error as string)
    } else {
      toast.success(t("backoffice.scheduledReports.editSuccess"))
      navigate({
        to: "/backoffice/scheduledReports/list",
        search: BackofficeScheduledReportsSearchSchema.parse({}),
      })
    }
  }

  if (!scheduledReport) {
    return null
  }

  return (
    <>
      <HeaderButtons>
        {scheduledReport.isActive ? (
          <Button
            variant={"destructive"}
            className="flex gap-2"
            onClick={() => mutateAsync({ ...scheduledReport, isActive: false })}
            disabled={mutateIsPending}
          >
            <Pause />
            <span>{t("backoffice.scheduledReports.pause")}</span>
          </Button>
        ) : (
          <Button
            className="flex gap-2"
            onClick={() => mutateAsync({ ...scheduledReport, isActive: true })}
            disabled={mutateIsPending}
          >
            <ArchiveRestore />
            <span>{t("backoffice.scheduledReports.reactivate")}</span>
          </Button>
        )}
      </HeaderButtons>
      {!scheduledReport.isActive && (
        <Card className="bg-red-100 border-red-200 p-2">
          <CardDescription className="text-red-900">
            {t("backoffice.scheduledReports.delete.disclaimer")}
          </CardDescription>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle>{t("backoffice.scheduledReports.edit.title")}</CardTitle>
          <CardDescription>{t("backoffice.scheduledReports.edit.description")}</CardDescription>
        </CardHeader>
        {isPending ? (
          <Loading />
        ) : (
          <BackofficeScheduledReportForm
            scheduledReport={scheduledReport}
            onSubmit={onSubmit}
            submitCopy={t("backoffice.scheduledReports.edit.save")}
            isPending={mutateIsPending}
            error={error}
          />
        )}
      </Card>
    </>
  )
}
